import { HERITIER_INCONNU, LIBELLES_STATUT_ECHEANCE } from './libelles.js';
import type { Argent, Calendrier, CodeRegle, CorpsRegles, JeuRegles, LigneHeritier, VueClient } from './types.js';

const FORMAT_ENTIER = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const FORMAT_CENTIMES = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Guadeloupe' });

/** Montant en centimes -> « 1 500 € » ou « 750,50 € ». */
export function euros(montant: Argent): string {
  const valeur = montant.amountCents / 100;
  return (Number.isInteger(valeur) ? FORMAT_ENTIER : FORMAT_CENTIMES).format(valeur);
}

/** Date `AAAA-MM-JJ` ou instant ISO -> `JJ/MM/AAAA` (fuseau de la Guadeloupe pour un instant). */
export function dateFr(valeur: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(valeur)) {
    const [a, m, j] = valeur.split('-');
    return `${j}/${m}/${a}`;
  }
  return FORMAT_DATE.format(new Date(valeur));
}

const estArgent = (v: unknown): v is Argent =>
  typeof v === 'object' && v !== null && Number.isSafeInteger((v as Argent).amountCents) && (v as Argent).currency === 'EUR';

const lienSur = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  if (v.startsWith('/') && !v.startsWith('//') && !v.startsWith('/\\')) return v;
  return v.startsWith('https://') ? v : null;
};

export const TITRE_A_JOUR = 'Vous êtes à jour';
export const TITRE_VERIFICATION = 'Votre situation est en cours de vérification auprès du Cabinet MAAT';

/**
 * Vue client (RG-17) : liste blanche des seuls champs du contrat client. Tout autre champ de la réponse (somme réglée,
 * nom, total encaissé) est ignoré, même si un serveur l'envoyait par erreur. « À jour » exige l'absence de reste dû.
 */
export function vueClient(brut: unknown): VueClient {
  const o = (typeof brut === 'object' && brut !== null ? brut : {}) as Record<string, unknown>;
  const reste = estArgent(o['remainingDue']) ? o['remainingDue'] : null;
  const aJour = o['upToDate'] === true && reste === null;
  const echeance = o['nextInstallment'] as { dueAt?: unknown; amount?: unknown } | null | undefined;
  const prochaine =
    !aJour && echeance && typeof echeance.dueAt === 'string' && estArgent(echeance.amount)
      ? `${euros(echeance.amount)} avant le ${dateFr(echeance.dueAt)}`
      : null;
  return {
    aJour,
    titre: aJour ? TITRE_A_JOUR : reste ? `Il vous reste à régler ${euros(reste)}` : TITRE_VERIFICATION,
    resteDu: aJour || !reste ? null : euros(reste),
    prochaine,
    recus: lienSur(o['receiptsUrl']),
  };
}

/** Corps de `facturation.regles` après avoir basculé une règle ; une règle absente du jeu est ajoutée, cochée. */
export function corpsApresBascule(jeu: JeuRegles, code: CodeRegle, raison: string): CorpsRegles {
  const existe = jeu.items.some((i) => i.ruleCode === code);
  const items = existe
    ? jeu.items.map((i) =>
        i.ruleCode === code ? { ...i, enabled: !i.enabled, params: { ...i.params } } : { ...i, params: { ...i.params } },
      )
    : [...jeu.items.map((i) => ({ ...i, params: { ...i.params } })), { ruleCode: code, enabled: true, params: {} }];
  return { reason: raison, items };
}

export interface LigneEcheance {
  id: string;
  date: string;
  montant: string;
  statut: string;
  manuel: boolean;
  aRegler: boolean;
}
export interface GroupeEcheances {
  personId: string;
  nom: string;
  echeances: LigneEcheance[];
}

/** Échéances groupées par héritier (ordre de la première échéance), chacune triée par date. */
export function echeancesParHeritier(calendrier: Calendrier, lignes: readonly LigneHeritier[]): GroupeEcheances[] {
  const triees = [...calendrier.installments].sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  const groupes = new Map<string, GroupeEcheances>();
  for (const e of triees) {
    const groupe = groupes.get(e.personId) ?? {
      personId: e.personId,
      nom: lignes.find((l) => l.personId === e.personId)?.displayName ?? HERITIER_INCONNU,
      echeances: [],
    };
    groupe.echeances.push({
      id: e.id,
      date: dateFr(e.dueAt),
      montant: euros(e.amount),
      statut: LIBELLES_STATUT_ECHEANCE[e.status] ?? e.status,
      manuel: e.isManualOverride,
      aRegler: e.status !== 'payee',
    });
    groupes.set(e.personId, groupe);
  }
  return [...groupes.values()];
}
